import { useEffect, useRef, useState } from "react";
import { createLogger } from "@/logging";
import { isAbortError } from "@/utils";
import { resolveLoadErrorMessage, type LoadState } from "../../loadState";
import type { EpisodeDiagnosticDetail } from "../diagnosticsSeries";
import { loadEpisodeTimelineFor, type EpisodeTimelineResult } from "./timelineArtifact";

const logger = createLogger("components/dataset/diagnostics");

const IDLE: LoadState<EpisodeTimelineResult> = { status: "idle" };
const LOADING: LoadState<EpisodeTimelineResult> = { status: "loading" };

/**
 * 구간별 운동 지표 본문을 **보기를 켤 때만** 로드한다(3번째 RTT).
 *
 * 본문은 창원 ep26 실측 145 KB 다. 그래서 다른 보기를 보는 동안 미리 받을 이유가 없다.
 * 한 번 받은 뒤에는 보기를 껐다 켜도 다시 받지 않는다. 에피소드가 바뀌면 서명 URL 도
 * 바뀌므로 새로 받는다.
 *
 * Args:
 *   detail: 첫 RTT 결과. null 이면 진단이 없다.
 *   enabled: 지금 이 보기를 보고 있는가.
 */
export function useEpisodeTimeline(
  detail: EpisodeDiagnosticDetail | null,
  enabled: boolean,
): LoadState<EpisodeTimelineResult> {
  // 서명 URL 은 응답마다 바뀐다. 그래서 에피소드가 바뀌었는지 판단하는 키로 쓴다.
  const key =
    detail === null
      ? null
      : `${detail.timelineSeries?.status ?? ""}|${detail.timelineSeries?.url ?? ""}|${detail.timelineSeries?.failure?.code ?? ""}`;
  const [keyed, setKeyed] = useState<{
    key: string | null;
    state: LoadState<EpisodeTimelineResult>;
  }>({ key: null, state: IDLE });
  // 보기를 바꿀 때마다 같은 본문을 다시 받지 않으려고 시작한 키를 기억한다. 요청이 끝나기
  // 전에 끊기면 키를 비운다. 그러면 다음 진입에서 다시 받는다.
  const startedKey = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || key === null) {
      return;
    }
    if (startedKey.current === key) {
      return;
    }
    startedKey.current = key;
    const controller = new AbortController();
    const { signal } = controller;
    let settled = false;
    setKeyed({ key, state: LOADING });

    async function load(): Promise<void> {
      try {
        const result = await loadEpisodeTimelineFor(detail, { signal });
        if (signal.aborted) return;
        settled = true;
        setKeyed({ key, state: { status: "success", data: result } });
      } catch (error) {
        if (signal.aborted || isAbortError(error)) return;
        logger.error("loadEpisodeTimelineFor failed", error);
        settled = true;
        // 실패한 키는 기억하지 않는다. "다시 시도"가 상세를 다시 받으면 서명 URL 이 같아도
        // 새 상세가 이 effect 를 다시 실행한다. 그러면 effect 가 본문을 다시 받는다.
        startedKey.current = null;
        setKeyed({ key, state: { status: "error", message: resolveLoadErrorMessage(error) } });
      }
    }

    void load();
    return () => {
      controller.abort();
      if (!settled) {
        startedKey.current = null;
      }
    };
  }, [detail, enabled, key]);

  if (keyed.key !== key) {
    return enabled ? LOADING : IDLE;
  }
  return keyed.state;
}
